import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play, FileText, FileType, Mic, Link, FileType2, Star } from 'lucide-react';
import { buildSignupUrl } from '../../lib/signupUrl';

const Hero = () => {
  const [spotsLeft, setSpotsLeft] = useState(54);
  const [viewersNow, setViewersNow] = useState(147);
  const signupUrl = buildSignupUrl();

  useEffect(() => {
    // Simulate live viewers
    const interval = setInterval(() => {
      setViewersNow(prev => prev + Math.floor(Math.random() * 5) - 2);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const contentTypes = [
    { icon: FileText, label: 'Blog Posts' },
    { icon: FileType, label: 'PDFs' },
    { icon: Mic, label: 'Podcasts' },
    { icon: Link, label: 'URLs' },
    { icon: FileType2, label: 'Plain Text' },
  ];

  return (
    <section className="relative min-h-screen pt-40 pb-20 px-6 overflow-hidden" data-testid="hero-section">
      {/* Background Glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-brand-primary/10 rounded-full blur-[150px]"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand-accent/5 rounded-full blur-[100px]"></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Spots Badge */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-8"
        >
          <div className="glass rounded-full px-4 py-2 flex items-center gap-3 text-sm">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <span className="text-gray-300"><span className="text-white font-bold">{spotsLeft}</span> spots left this month</span>
            </span>
            <span className="text-gray-500">·</span>
            <span className="text-gray-300"><span className="text-white font-bold">{viewersNow}</span> people viewing now</span>
          </div>
        </motion.div>

        {/* Main Headline */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-heading font-black tracking-tight leading-[1.1] text-white mb-6">
            You write great content.
            <br />
            <span className="text-gray-400">Nobody sees it.</span>
            <br />
            <span className="text-gradient">Video fixes that.</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Paste any blog post → AdoriAI turns it into a{' '}
            <span className="text-white font-semibold">polished, shareable video</span>{' '}
            in under 2 minutes. Grow your audience without learning video editing.
          </p>
        </motion.div>

        {/* Content Types */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-3 mb-10"
        >
          <span className="text-sm text-gray-500 mr-2 self-center">Works with</span>
          {contentTypes.map((type, index) => (
            <span 
              key={index}
              className="flex items-center gap-2 bg-surface-elevated/50 border border-white/10 px-4 py-2 rounded-full text-sm text-gray-300"
            >
              <type.icon className="w-4 h-4 text-brand-accent" />
              {type.label}
            </span>
          ))}
        </motion.div>

        {/* CTA Buttons */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10"
        >
          <a
            href={signupUrl}
            className="flex items-center gap-3 bg-brand-primary hover:bg-brand-primary-hover text-surface-base px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 hover:-translate-y-1 glow-primary-strong"
            data-testid="hero-cta-button"
          >
            Start free trial
            <ArrowRight className="w-5 h-5" />
          </a>
          <a
            href="#how-it-works"
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors font-medium"
            data-testid="hero-secondary-cta"
          >
            <div className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center">
              <Play className="w-4 h-4 fill-current" />
            </div>
            See how it works
          </a>
        </motion.div>

        {/* Social Proof */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          {/* Avatar Stack */}
          <div className="flex -space-x-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div 
                key={i}
                className="w-10 h-10 rounded-full border-2 border-surface-base bg-surface-elevated overflow-hidden"
                style={{
                  backgroundImage: `url(https://i.pravatar.cc/100?img=${i + 10})`,
                  backgroundSize: 'cover'
                }}
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex text-yellow-400">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className="w-5 h-5 fill-current" />
              ))}
            </div>
            <span className="text-gray-300">
              Trusted by <span className="text-white font-bold">10,000+</span> solo creators & bloggers
            </span>
          </div>
        </motion.div>

        {/* Video Preview Mockup */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-16 max-w-4xl mx-auto"
        >
          <div className="glass rounded-2xl p-6 border border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex gap-1.5">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
              </div>
              <span className="text-xs text-gray-500 ml-2">adoriai.com/editor</span>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 relative">
              {/* Input */}
              <div className="bg-surface-elevated rounded-xl p-4 border border-white/5">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">Blog Post</div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-700/50 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-700/50 rounded w-full"></div>
                  <div className="h-3 bg-gray-700/50 rounded w-5/6"></div>
                  <div className="h-3 bg-gray-700/50 rounded w-2/3"></div>
                </div>
              </div>

              {/* Arrow - Centered between the two cards */}
              <div className="hidden md:flex items-center justify-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="w-12 h-12 rounded-full bg-brand-primary flex items-center justify-center glow-primary shadow-lg">
                  <ArrowRight className="w-6 h-6 text-surface-base" />
                </div>
              </div>

              {/* Output */}
              <div className="bg-surface-elevated rounded-xl p-4 border border-brand-primary/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/10 to-transparent"></div>
                <div className="relative">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">Video Preview</div>
                  <div className="aspect-video bg-surface rounded-lg flex items-center justify-center relative">
                    <div className="w-16 h-16 rounded-full bg-brand-primary/80 flex items-center justify-center cursor-pointer hover:scale-110 transition-transform glow-primary">
                      <Play className="w-8 h-8 text-surface-base fill-current ml-1" />
                    </div>
                    <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
                      <div className="h-1 flex-1 bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full w-3/4 bg-brand-primary rounded-full"></div>
                      </div>
                      <span className="text-xs text-gray-400">1:48</span>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-sm text-green-400">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                    Video ready · 1 min 48 sec
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
